import React, { ReactNode, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AccessPermission } from '../types/adminAccess';
import {
  LayoutDashboard,
  Users,
  Mail,
  ClipboardCheck,
  Calendar,
  PlayCircle,
  LogOut,
  Menu,
  X,
  Shield,
  Link as LinkIcon,
  Newspaper,
  Flag,
  Megaphone,
  History,
  Wallet,
  Building2,
  Trophy,
  MessageCircle,
  Settings,
  BarChart2,
  FileText,
  Workflow,
  Bot
} from 'lucide-react';
import logo from '../assets/logo.png';

interface LayoutProps {
  children: ReactNode;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  access: AccessPermission;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, logout, adminProfile, hasAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isWhatsAppRoute = location.pathname.startsWith('/whatsapp');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navigation: NavigationItem[] = [
    {
      name: 'Dashboard',
      href: '/home',
      icon: LayoutDashboard,
      access: 'dashboard' as AccessPermission,
    },
    {
      name: 'User Management',
      href: '/users',
      icon: Users,
      access: 'users' as AccessPermission,
    },
    {
      name: 'Email Center',
      href: '/emails',
      icon: Mail,
      access: 'emails' as AccessPermission,
    },
    {
      name: 'Attendance',
      href: '/attendance',
      icon: ClipboardCheck,
      access: 'attendance' as AccessPermission,
    },
    {
      name: 'Event Management',
      href: '/events',
      icon: Calendar,
      access: 'events' as AccessPermission,
    },
    {
      name: 'Video Verification',
      href: '/videos',
      icon: PlayCircle,
      access: 'videos' as AccessPermission,
    },
    {
      name: 'Connection Requests',
      href: '/connections',
      icon: LinkIcon,
      access: 'connections' as AccessPermission,
    },
    {
      name: 'Announcements',
      href: '/announcements',
      icon: Megaphone,
      access: 'announcements' as AccessPermission,
    },
    {
      name: 'Selection News',
      href: '/news',
      icon: Newspaper,
      access: 'news' as AccessPermission,
    },
    {
      name: 'Sponsor Stories',
      href: '/sponsor-stories',
      icon: Trophy,
      access: 'sponsor_stories' as AccessPermission,
    },
    {
      name: 'WhatsApp Inbox',
      href: '/whatsapp',
      icon: MessageCircle,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'WhatsApp Contacts',
      href: '/whatsapp/contacts',
      icon: Users,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'Templates',
      href: '/whatsapp/templates',
      icon: FileText,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'Bulk Messaging',
      href: '/whatsapp/broadcasts',
      icon: Megaphone,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'Sent Messages',
      href: '/whatsapp/sent',
      icon: History,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'Billing',
      href: '/whatsapp/billing',
      icon: Wallet,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'Template Analytics',
      href: '/whatsapp/analytics',
      icon: BarChart2,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'ConvoBot',
      href: '/whatsapp/convobot',
      icon: Bot,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'WhatsApp Integrations',
      href: '/whatsapp/integrations',
      icon: Workflow,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'WhatsApp Settings',
      href: '/whatsapp/settings',
      icon: Settings,
      access: 'whatsapp' as AccessPermission,
    },
    {
      name: 'Content Reports',
      href: '/reports',
      icon: Flag,
      access: 'reports' as AccessPermission,
    },
    {
      name: 'Organization Management',
      href: '/organization',
      icon: Building2,
      access: 'organization_management' as AccessPermission,
    },
  ].filter((item) => hasAccess(item.access));

  const NavItem: React.FC<{ item: typeof navigation[0] }> = ({ item }) => (
    <NavLink
      to={item.href}
      className={({ isActive }) =>
        `flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${isActive
          ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
        }`
      }
      onClick={() => setSidebarOpen(false)}
    >
      <item.icon className="mr-3 h-5 w-5" />
      {item.name}
    </NavLink>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 transition-opacity lg:hidden z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 z-50 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <img src={logo} alt="AmaPlayer" className="w-9 h-9 rounded-lg object-contain" />
              <div>
                <h1 className="text-lg font-semibold text-gray-900">AmaPlayer</h1>
                <p className="text-xs text-gray-500">Admin Dashboard</p>
              </div>
            </div>
            <button
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-2">
            {navigation.map((item) => (
              <NavItem key={item.name} item={item} />
            ))}
          </nav>

          {/* User info and logout */}
          <div className="p-4 border-t">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Shield className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {currentUser?.email}
                </p>
                <p className="text-xs text-gray-500">
                  {adminProfile?.role === 'superAdmin' ? 'super admin' : adminProfile?.role || 'Administrator'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200 lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-2">
              <img src={logo} alt="AmaPlayer" className="w-7 h-7 rounded-lg object-contain" />
              <h1 className="text-lg font-semibold text-gray-900">Admin Dashboard</h1>
            </div>
            <div className="w-9" /> {/* Spacer for alignment */}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {isWhatsAppRoute ? (
            <div className="wa-theme h-full">
              {children}
            </div>
          ) : (
            <div className="p-6">
              {children}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Layout;
